import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Paper, CircularProgress, Alert, TextField } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/hu';
import { styled } from '@mui/system';
import { useAuth } from '@clerk/clerk-react';

// Az API alap-címe: éles oldalon üres (az nginx továbbítja az /api kéréseket), fejlesztéskor a REACT_APP_API_URL-ből jön
const API = process.env.REACT_APP_API_URL ?? "";

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    backgroundColor: theme.palette.mode === 'dark' ? '#1d1d1d' : '#ffffff',
    color: theme.palette.text.primary,
}));

const AdminKepzesIdopontok: React.FC = () => {
    const { getToken } = useAuth();
    // Két különböző állapot a két dátumhoz
    const [selectedDate1, setSelectedDate1] = useState<Dayjs | null>(null);
    const [selectedDate2, setSelectedDate2] = useState<Dayjs | null>(null);
    const [location, setLocation] = useState<string>(''); // ÚJ: Helyszín state
    const [eventUrl, setEventUrl] = useState<string>(''); // ÚJ: URL state
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const formatDateForApi = (date: Dayjs | null): string | null => {
        return date ? date.format('YYYY.MM.DD.') : null;
    };

    // Aktuális adatok lekérése az API-ból
    useEffect(() => {
        const fetchCurrentData = async () => {
            setLoading(true);
            setError(null);
            const token = await getToken();
            if (!token) {
                setError('A felhasználó nincs bejelentkezve, vagy a munkamenet lejárt.');
                setLoading(false);
                return;
            }
            try {
                const response = await fetch(`${API}/api/user/getidopont`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    throw new Error('Hiba az adatok lekérésekor.');
                }
                const data = await response.json();

                // Dátumok ellenőrzése és beállítása
                if (data.idopont1 && dayjs(data.idopont1, 'YYYY.MM.DD.').isValid()) {
                    setSelectedDate1(dayjs(data.idopont1, 'YYYY.MM.DD.'));
                }
                if (data.idopont2 && dayjs(data.idopont2, 'YYYY.MM.DD.').isValid()) {
                    setSelectedDate2(dayjs(data.idopont2, 'YYYY.MM.DD.'));
                }
                // ÚJ: Helyszín és URL beállítása
                if (data.location) {
                    setLocation(data.location);
                }
                if (data.eventUrl) {
                    setEventUrl(data.eventUrl);
                }

            } catch (err: any) {
                setError(err.message || 'Ismeretlen hiba történt az adatok lekérésekor.');
            } finally {
                setLoading(false);
            }
        };
        fetchCurrentData();
    }, []);

    // Változások mentése POST kéréssel
    const handleSaveChanges = async () => {
        if (!selectedDate1 || !selectedDate2) {
            setError('Kérlek, válaszd ki mindkét dátumot!');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        const token = await getToken();
        if (!token) {
            setError('A felhasználó nincs bejelentkezve, vagy a munkamenet lejárt.');
            setLoading(false);
            return;
        }

        const formattedDate1 = formatDateForApi(selectedDate1);
        const formattedDate2 = formatDateForApi(selectedDate2);

        try {
            const response = await fetch(`${API}/api/admin/idopontvaltoztatas`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    idopont1: formattedDate1,
                    idopont2: formattedDate2,
                    location: location, // ÚJ: helyszín
                    eventUrl: eventUrl, // ÚJ: URL
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Sikertelen mentés. Kérlek, próbáld újra.');
            }

            setSuccess('Adatok sikeresen elmentve!');
        } catch (err: any) {
            setError(err.message || 'Sikertelen mentés. Kérlek, ellenőrizd a kapcsolatot.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <StyledPaper elevation={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CalendarTodayIcon sx={{ mr: 2 }} />
                <Typography variant="h6" component="h2">
                    Képzés adatainak módosítása
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
            ) : (
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="hu">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Typography variant="subtitle1" sx={{ mt: 2 }}>Dátumok</Typography>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                            <DatePicker
                                label="Ettől a dátumtól"
                                value={selectedDate1}
                                onChange={(newValue) => setSelectedDate1(newValue)}
                                slotProps={{
                                    textField: { variant: 'outlined' },
                                }}
                            />
                            <DatePicker
                                label="Eddig a dátumig"
                                value={selectedDate2}
                                onChange={(newValue) => setSelectedDate2(newValue)}
                                slotProps={{
                                    textField: { variant: 'outlined' },
                                }}
                            />
                        </Box>
                        
                        <Typography variant="subtitle1" sx={{ mt: 2 }}>Helyszín és Link</Typography>
                        <TextField
                            label="Helyszín"
                            variant="outlined"
                            fullWidth
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                        />
                        <TextField
                            label="Esemény link (URL)"
                            variant="outlined"
                            fullWidth
                            value={eventUrl}
                            onChange={(e) => setEventUrl(e.target.value)}
                        />
                    </Box>
                </LocalizationProvider>
            )}

            {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
            
            <Button
                variant="contained"
                color="primary"
                onClick={handleSaveChanges}
                disabled={loading}
                sx={{ mt: 3 }}
            >
                Változtatások elmentése
            </Button>
        </StyledPaper>
    );
};

export default AdminKepzesIdopontok;